import { FileShareView } from "@/components/features/file-share/FileShareView";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Input,
  Typography,
} from "@/components/ui";
import { useAccessMatterShareFolder } from "@/lib/data/react-query/matters/matter-share-folder";
import { queryKeys } from "@/lib/data/static/query-keys";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { FileText, Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const fileFormSchema = z.object({
  passcode: z.string().min(1, { message: "Passcode is required" }),
});

type FileFormData = z.infer<typeof fileFormSchema>;

export const Route = createFileRoute("/file-share")({
  component: FileSharePortal,
  validateSearch: z.object({
    passcode: z.string().optional().catch(""),
  }),
});

function FileSharePortal() {
  const queryClient = useQueryClient();
  const { passcode: accessKey } = Route.useSearch();
  queryClient.removeQueries({ queryKey: [queryKeys.currentUser], exact: true });
  const [passcode, setPasscode] = useState("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const { data, isLoading, error, isFetching } = useQuery({
    ...useAccessMatterShareFolder({
      shareFolderPasscode: passcode,
    }),
    enabled: !!passcode,
  });

  const form = useForm<FileFormData>({
    resolver: zodResolver(fileFormSchema),
    defaultValues: {
      passcode: "",
    },
  });

  useEffect(() => {
    if (accessKey && form.watch("passcode") !== accessKey) {
      form.setValue("passcode", accessKey);
    }
  }, [accessKey, form]);

  const handleBack = () => {
    const previousPasscode = passcode;
    setPasscode("");
    setErrorMessage("");
    form.reset({ passcode: "" });
    if (previousPasscode) {
      queryClient.removeQueries({
        queryKey: [queryKeys.matterShareFolders, previousPasscode],
        exact: true,
      });
    }
  };

  const onSubmit = (data: FileFormData) => {
    setPasscode(data.passcode);
    // Don't clear error message here - let useEffect handle it based on the query result
  };

  // Handle errors in useEffect to prevent infinite re-renders
  useEffect(() => {
    if (error) {
      if (error instanceof AxiosError) {
        const status = error.response?.status;
        if (status === 401) {
          setErrorMessage(
            "Invalid passcode. Please check your passcode and try again.",
          );
        } else {
          setErrorMessage(error.response?.data.meta.userMsg as string);
        }
      } else {
        setErrorMessage("An unexpected error occurred. Please try again.");
      }
    } else if (!isFetching && !isLoading) {
      // Only clear error message when there's no error and we're not fetching
      setErrorMessage("");
    }
  }, [error, isFetching, isLoading]);

  const files = data?.data;

  useEffect(() => {
    if (
      Array.isArray(files) &&
      files.length === 0 &&
      !isFetching &&
      !isLoading
    ) {
      const previousPasscode = passcode;
      setPasscode("");
      form.reset({ passcode: "" });
      if (data)
        setErrorMessage("The share folder you're trying to access is empty.");
      if (previousPasscode) {
        queryClient.removeQueries({
          queryKey: [queryKeys.matterShareFolders, previousPasscode],
          exact: true,
        });
      }
    }
  }, [data, files, form, isFetching, isLoading, passcode, queryClient]);

  // If we have data, show the file share view
  if (Array.isArray(files) && files.length > 0) {
    return (
      <FileShareView
        files={files}
        onBack={handleBack}
        shareFolderPasscode={passcode}
      />
    );
  }

  // Otherwise show the form
  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="w-full max-w-md">
        <Card className="border-0 shadow-xl">
          <CardHeader className="space-y-4 pb-6 text-center">
            <div className="bg-primary/10 mx-auto flex h-16 w-16 items-center justify-center rounded-full">
              <FileText className="text-primary h-8 w-8" />
            </div>
            <div className="space-y-2">
              <Typography variant="heading" size="lg">
                File sharing portal
              </Typography>
              <Typography variant="default" className="text-muted-foreground">
                Enter your passcode to access the shared files
              </Typography>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="passcode" className="text-sm font-medium">
                  Passcode
                </label>
                <div className="relative">
                  <Input
                    id="passcode"
                    type="text"
                    placeholder="Enter your passcode"
                    {...form.register("passcode")}
                    className="pl-10"
                  />
                  <Lock className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform" />
                </div>
                {form.formState.errors.passcode && (
                  <Typography
                    variant="default"
                    className="text-destructive text-sm"
                  >
                    {form.formState.errors.passcode.message}
                  </Typography>
                )}
                {errorMessage && (
                  <Typography
                    variant="default"
                    className="text-destructive text-sm"
                  >
                    {errorMessage}
                  </Typography>
                )}
              </div>
              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={isFetching}
              >
                {isLoading ? "Loading..." : "Access shared files"}
              </Button>
            </form>

            <div className="text-center">
              <Typography
                variant="default"
                className="text-muted-foreground text-sm"
              >
                Need help? Contact your legal representative
              </Typography>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
